/**
 * ``TemplateEditor`` — single-template editor for the v1-style flow.
 *
 * Layout: 2-column on lg+ (left = collapsible spec editor, right = preview
 * + meta). The form is structured as three ``<details>`` blocks the v1
 * editor used: **Chrome / Paper / Voice**. Only the block matching the
 * loaded template's ``kind`` is expanded by default and editable — the
 * other two are surfaced read-only because a single template only carries
 * one kind of spec at a time (you can't morph a chrome row into a paper
 * row server-side; the BE keeps ``kind`` immutable on PATCH).
 *
 * Spec edit surface:
 *   1. ``SchemaFormEditor`` — guided form for the canonical knobs.
 *   2. Raw JSON ``<textarea>`` escape hatch (live JSON.stringify, controlled
 *      via React state) — round-2 ask, no Monaco / CodeMirror per spec.
 *
 * Save / discard:
 *   - Save: floating bottom-right ``<Button>``; disabled until the form
 *     is dirty (diff vs the loaded baseline) AND the JSON parses cleanly.
 *   - Discard: opens a confirm ``<Dialog>`` only when the form is dirty —
 *     otherwise navigates straight away.
 *
 * Built-ins are read-only: the editor renders a "Personalizza per
 * modificare" banner at the top and disables every input. The parent
 * (route) is responsible for the "fork into custom" affordance via the
 * grid list — we don't duplicate the create flow here.
 */

import * as React from "react";

import { Trans, useLingui } from "@lingui/react/macro";
import { msg } from "@lingui/core/macro";

import {
  ArrowLeft,
  ChevronDown,
  Save,
  Sparkles,
  Trash2,
} from "lucide-react";

import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  cn,
} from "@ando/ui";
import type { TemplateDetail, TemplateKind } from "@ando/core";

import { SchemaFormEditor } from "./SchemaFormEditor";
import { TemplatePreview } from "./TemplatePreview";

const KIND_LABEL: Record<TemplateKind, ReturnType<typeof msg>> = {
  chrome: msg`Chrome · slide deck`,
  paper: msg`Paper · documento`,
  writing_style: msg`Voice · stile di scrittura`,
};

export interface TemplateEditorProps {
  /** Loaded template detail — drives the form's baseline state. */
  readonly detail: TemplateDetail;
  /** When ``true``, every input + the save button are disabled. */
  readonly readOnly?: boolean;
  /**
   * Set while a save mutation is in flight — disables Save + dims the
   * preview overlay so the user knows the on-disk PNG lags behind.
   */
  readonly isSaving?: boolean;
  /** Optional delete handler — surfaces the destructive button. */
  readonly onDelete?: () => void;
  /** Optional "go back" handler — wired to the list-route by the parent. */
  readonly onBack?: () => void;
  /**
   * Save handler — receives the patched detail snapshot. The parent owns
   * the mutation + cache invalidation; this component is presentational.
   */
  readonly onSave: (next: {
    readonly name: string;
    readonly description: string;
    readonly spec: Record<string, unknown>;
  }) => Promise<void> | void;
}

interface FormState {
  readonly name: string;
  readonly description: string;
  readonly specJson: string;
  /** Parsed-back-to-object spec — ``null`` when JSON is invalid. */
  readonly parsedSpec: Record<string, unknown> | null;
  readonly parseError: string | null;
}

function buildState(detail: TemplateDetail): FormState {
  return {
    name: detail.name,
    description: detail.description,
    specJson: JSON.stringify(detail.spec, null, 2),
    parsedSpec: detail.spec,
    parseError: null,
  };
}

export const TemplateEditor: React.FC<TemplateEditorProps> = ({
  detail,
  readOnly = false,
  isSaving = false,
  onDelete,
  onBack,
  onSave,
}) => {
  const { t, i18n } = useLingui();
  const baseline = React.useMemo(() => buildState(detail), [detail]);
  const [form, setForm] = React.useState<FormState>(baseline);
  const [confirmDiscard, setConfirmDiscard] = React.useState(false);
  const [confirmDelete, setConfirmDelete] = React.useState(false);

  // Re-sync local state when a fresh detail arrives (e.g. after the parent
  // refetches following a save). React's reference equality on ``detail``
  // is enough: the parent threads the same object until the cache updates.
  React.useEffect(() => {
    setForm(buildState(detail));
  }, [detail]);

  const dirty =
    form.name !== baseline.name ||
    form.description !== baseline.description ||
    form.specJson !== baseline.specJson;

  function patchSpecJson(nextJson: string) {
    let parsed: Record<string, unknown> | null = null;
    let parseError: string | null = null;
    try {
      const candidate = JSON.parse(nextJson);
      if (
        typeof candidate !== "object" ||
        candidate === null ||
        Array.isArray(candidate)
      ) {
        parseError = t`Lo spec deve essere un oggetto JSON.`;
      } else {
        parsed = candidate as Record<string, unknown>;
      }
    } catch (err) {
      parseError = err instanceof Error ? err.message : t`JSON non valido`;
    }
    setForm((prev) => ({
      ...prev,
      specJson: nextJson,
      parsedSpec: parsed,
      parseError,
    }));
  }

  function patchSpecObject(next: Record<string, unknown>) {
    setForm((prev) => ({
      ...prev,
      specJson: JSON.stringify(next, null, 2),
      parsedSpec: next,
      parseError: null,
    }));
  }

  async function handleSave() {
    if (!form.parsedSpec) return;
    if (!form.name.trim()) return;
    await onSave({
      name: form.name.trim(),
      description: form.description.trim(),
      spec: form.parsedSpec,
    });
  }

  function handleBack() {
    if (dirty) {
      setConfirmDiscard(true);
    } else {
      onBack?.();
    }
  }

  const canSave = dirty && form.parsedSpec != null && form.name.trim().length > 0;

  // Which ``<details>`` block to open by default: the one matching the
  // template's own kind. The two other blocks are surfaced collapsed +
  // read-only as a navigational hint that a separate template carries
  // those specs (helps the user understand the BE model without a context
  // switch out of the route).
  return (
    <div className="relative flex flex-col gap-6 px-8 pt-6 pb-24">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 flex-1 flex-col gap-2">
          {onBack ? (
            <button
              type="button"
              onClick={handleBack}
              className="inline-flex w-fit items-center gap-1 text-[12px] text-ink-3 hover:text-ink-1"
            >
              <ArrowLeft size={12} aria-hidden />
              <Trans>Tutti i template</Trans>
            </button>
          ) : null}
          {readOnly ? (
            <>
              <h2 className="truncate font-semibold text-[20px] text-ink-1">
                {detail.name}
              </h2>
              <p className="text-[12.5px] text-ink-3">{detail.description}</p>
            </>
          ) : (
            <div className="flex flex-col gap-2">
              <label className="flex flex-col gap-1">
                <span className="font-medium text-ink-2 text-xs"><Trans>Nome</Trans></span>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, name: e.target.value }))
                  }
                  maxLength={64}
                  aria-label={t`Nome del template`}
                  className="w-full max-w-md rounded-md border border-hairline bg-card px-2.5 py-1.5 font-semibold text-[16px] text-ink-1 focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="font-medium text-ink-2 text-xs">
                  <Trans>Descrizione</Trans>
                </span>
                <textarea
                  value={form.description}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, description: e.target.value }))
                  }
                  rows={2}
                  maxLength={500}
                  aria-label={t`Descrizione del template`}
                  className="w-full rounded-md border border-hairline bg-card px-2.5 py-1.5 text-[12.5px] text-ink-2 focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </label>
            </div>
          )}
        </div>
      </header>

      {readOnly ? (
        <div className="flex items-start gap-2 rounded-md border border-accent/40 border-dashed bg-accent/5 p-3 text-[12px] text-ink-2">
          <Sparkles size={14} className="mt-0.5 shrink-0 text-accent" aria-hidden />
          <span>
            <Trans>
              Questo template è built-in e in sola lettura. Usa "Personalizza
              per modificare" dalla griglia per crearne una copia modificabile.
            </Trans>
          </span>
        </div>
      ) : null}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[2fr_1fr]">
        <div className="flex flex-col gap-4">
          {/* Chrome / Paper / Voice — one <details> block per kind. Only
              the matching kind is interactive; the rest are surfaced
              collapsed + with a hint to direct the user back to the list. */}
          {(["chrome", "paper", "writing_style"] as const).map((kind) => {
            const isActive = detail.kind === kind;
            return (
              <details
                key={kind}
                open={isActive}
                className="rounded-lg border border-hairline bg-card/40 px-4 py-3"
              >
                <summary className="flex cursor-pointer items-center gap-2 font-medium text-[13px] text-ink-1">
                  <ChevronDown
                    size={14}
                    className="text-ink-3 transition-transform group-open:rotate-180"
                    aria-hidden
                  />
                  {i18n._(KIND_LABEL[kind])}
                  {!isActive ? (
                    <span className="ml-auto rounded bg-ink-1/[0.05] px-1.5 py-0.5 font-mono text-[9.5px] text-ink-3 tracking-wider">
                      <Trans>Sez. non applicabile</Trans>
                    </span>
                  ) : null}
                </summary>
                <div className="mt-3">
                  {isActive ? (
                    form.parsedSpec ? (
                      <SchemaFormEditor
                        kind={kind}
                        spec={form.parsedSpec}
                        onChange={patchSpecObject}
                        readOnly={readOnly}
                      />
                    ) : (
                      <p
                        role="alert"
                        className="text-[12px] text-warning"
                      >
                        <Trans>
                          Lo spec JSON non è valido; correggi il blocco JSON
                          sotto per riattivare l'editor.
                        </Trans>
                      </p>
                    )
                  ) : (
                    <p className="text-[12px] text-ink-3">
                      <Trans>
                        Questo template è di tipo{" "}
                        <strong>{i18n._(KIND_LABEL[detail.kind])}</strong>. Per
                        modificare le impostazioni {i18n._(KIND_LABEL[kind])},
                        apri un template di quella categoria dalla lista.
                      </Trans>
                    </p>
                  )}
                </div>
              </details>
            );
          })}

          {/* Raw JSON escape hatch — controlled textarea per the round-2 spec. */}
          <details className="rounded-lg border border-hairline bg-card/40 px-4 py-3">
            <summary className="flex cursor-pointer items-center gap-2 font-medium text-[13px] text-ink-1">
              <ChevronDown size={14} className="text-ink-3" aria-hidden />
              <Trans>Spec JSON avanzato</Trans>
              {form.parseError ? (
                <span role="alert" className="ml-2 text-[11px] text-warning">
                  {form.parseError}
                </span>
              ) : null}
            </summary>
            <div className="mt-3 flex flex-col gap-2">
              <p className="text-[11.5px] text-ink-3">
                <Trans>
                  Editor a basso livello: utile per chiavi annidate (es.
                  <code className="mx-1 rounded bg-ink-1/[0.05] px-1 py-0.5 font-mono text-[10.5px]">
                    decorations
                  </code>
                  ,
                  <code className="mx-1 rounded bg-ink-1/[0.05] px-1 py-0.5 font-mono text-[10.5px]">
                    slide_layout_overrides
                  </code>
                  ) non coperte dal form guidato.
                </Trans>
              </p>
              <textarea
                value={form.specJson}
                onChange={(e) => patchSpecJson(e.target.value)}
                disabled={readOnly}
                rows={18}
                spellCheck={false}
                aria-invalid={form.parseError != null}
                aria-label={t`Spec JSON`}
                className={cn(
                  "w-full resize-y rounded-md border border-hairline bg-card px-2.5 py-1.5 font-mono text-[11.5px] text-ink-1",
                  "focus:outline-none focus:ring-2 focus:ring-ring",
                  form.parseError ? "border-warning/60" : "",
                )}
              />
            </div>
          </details>
        </div>

        <aside className="flex flex-col gap-3 lg:sticky lg:top-4 lg:self-start">
          <TemplatePreview
            previewUrl={detail.preview_url}
            name={detail.name}
            isRefreshing={isSaving}
            caption={t`L'anteprima si aggiorna dopo il salvataggio (rigenerata server-side).`}
          />
          <div className="rounded-md border border-hairline bg-card/40 p-3 text-[11.5px] text-ink-3">
            <p>
              <strong className="text-ink-2"><Trans>Tipo:</Trans></strong>{" "}
              {i18n._(KIND_LABEL[detail.kind])}
            </p>
            <p>
              <strong className="text-ink-2"><Trans>Scope:</Trans></strong> {detail.scope}
            </p>
            {detail.slug ? (
              <p className="truncate">
                <strong className="text-ink-2"><Trans>Slug:</Trans></strong>{" "}
                <code className="font-mono text-[10.5px]">{detail.slug}</code>
              </p>
            ) : null}
          </div>

          {onDelete && !readOnly ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setConfirmDelete(true)}
            >
              <Trash2 size={12} aria-hidden />
              <Trans>Elimina template</Trans>
            </Button>
          ) : null}
        </aside>
      </div>

      {/* Floating Save button — disabled until the form is dirty + valid. */}
      {!readOnly ? (
        <div className="fixed right-8 bottom-6 z-30">
          <Button
            size="lg"
            onClick={() => void handleSave()}
            disabled={!canSave || isSaving}
            aria-disabled={!canSave || isSaving}
          >
            <Save size={14} aria-hidden />
            {isSaving ? t`Salvataggio…` : t`Salva modifiche`}
          </Button>
        </div>
      ) : null}

      <Dialog open={confirmDiscard} onOpenChange={setConfirmDiscard}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle><Trans>Modifiche non salvate</Trans></DialogTitle>
            <DialogDescription>
              <Trans>
                Hai modifiche pendenti. Tornando indietro andranno perse.
                Procedi?
              </Trans>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setConfirmDiscard(false)}
            >
              <Trans>Annulla</Trans>
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                setConfirmDiscard(false);
                onBack?.();
              }}
            >
              <Trans>Scarta e torna alla lista</Trans>
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle><Trans>Eliminare il template?</Trans></DialogTitle>
            <DialogDescription>
              <Trans>
                L'azione è irreversibile. Il template "{detail.name}" verrà
                rimosso e non sarà più disponibile nei drawer Studio.
              </Trans>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setConfirmDelete(false)}
            >
              <Trans>Annulla</Trans>
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                setConfirmDelete(false);
                onDelete?.();
              }}
            >
              <Trans>Elimina</Trans>
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
TemplateEditor.displayName = "TemplateEditor";
